import { Link } from "react-router-dom";
import { MapPin, Mail, Leaf, Facebook, Phone } from "lucide-react";
import { BRAND } from "@/data/site";

// TikTok isn't in lucide-react — use a lightweight inline SVG.
const TikTokIcon = ({ size = 18 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.85a8.16 8.16 0 0 0 4.77 1.52V6.94a4.85 4.85 0 0 1-1.84-.25z"/>
  </svg>
);

export default function Footer() {
  const socials = BRAND.socials || {};
  return (
    <footer data-testid="site-footer" className="bg-[#0A0F0B] border-t border-[#1F3325]">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-5">
            <img src={BRAND.logoUrl} alt="" className="h-14 w-14 object-contain" />
            <div>
              <div className="font-display uppercase text-2xl tracking-wider text-[#F0F4F1]">{BRAND.name}</div>
              <div className="font-display uppercase text-[10px] tracking-[0.35em] text-[#8DE038]">Established {BRAND.established} · {BRAND.area}</div>
            </div>
          </div>
          <p className="text-[#9CAEA3] max-w-md leading-relaxed">{BRAND.tagline} A trusted marketplace connecting customers with vetted local pros — gardening, joinery, painting &amp; property maintenance.</p>
          <div className="mt-6 inline-flex items-center gap-2 text-xs font-display uppercase tracking-widest text-[#8DE038]">
            <Leaf size={14} /> Vetted Pros · Trusted Locally · Built in Leeds
          </div>

          {(socials.facebook || socials.tiktok || socials.instagram) && (
            <div data-testid="footer-socials" className="mt-6 flex items-center gap-3">
              <span className="font-display uppercase text-[10px] tracking-[0.35em] text-[#9CAEA3] mr-1">Follow us</span>
              {socials.facebook && (
                <a href={socials.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="social-facebook" className="w-9 h-9 border border-[#1F3325] hover:border-[#8DE038] hover:text-[#8DE038] text-[#C8D4CB] flex items-center justify-center transition-colors">
                  <Facebook size={16}/>
                </a>
              )}
              {socials.tiktok && (
                <a href={socials.tiktok} target="_blank" rel="noreferrer" aria-label="TikTok" data-testid="social-tiktok" className="w-9 h-9 border border-[#1F3325] hover:border-[#8DE038] hover:text-[#8DE038] text-[#C8D4CB] flex items-center justify-center transition-colors">
                  <TikTokIcon size={16}/>
                </a>
              )}
            </div>
          )}
        </div>

        <div>
          <div className="font-display uppercase text-sm tracking-widest text-[#F0F4F1] mb-4">Customers</div>
          <ul className="space-y-2 text-[#9CAEA3]">
            <li><Link to="/services" className="hover:text-[#8DE038]">Services</Link></li>
            <li><Link to="/pricing" className="hover:text-[#8DE038]">Pricing</Link></li>
            <li><Link to="/book" className="hover:text-[#8DE038]">Book Now</Link></li>
            <li><Link to="/gallery" className="hover:text-[#8DE038]">Gallery</Link></li>
            <li><Link to="/login" className="hover:text-[#8DE038]">Sign in</Link></li>
          </ul>
        </div>

        <div>
          <div className="font-display uppercase text-sm tracking-widest text-[#F0F4F1] mb-4">Pros</div>
          <ul className="space-y-2 text-[#9CAEA3]">
            <li><Link to="/register?role=contractor" className="hover:text-[#8DE038]">Become a contractor</Link></li>
            <li><Link to="/contractor" className="hover:text-[#8DE038]">Contractor portal</Link></li>
            <li><Link to="/how-it-works" className="hover:text-[#8DE038]">How it works</Link></li>
          </ul>
          <div className="mt-6 flex items-start gap-2 text-[#9CAEA3]"><MapPin size={16} className="mt-1 text-[#8DE038]" /> Leeds · expanding nationally</div>
          <div className="mt-2 flex items-start gap-2 text-[#9CAEA3]"><Phone size={16} className="mt-1 text-[#8DE038]" />
            <div>
              <a href="tel:+447367025779" className="block hover:text-[#8DE038]">07367 025 779 <span className="text-[10px] uppercase tracking-widest text-[#6B7C71] ml-1">Mobile</span></a>
              <a href="tel:01135197348" className="block hover:text-[#8DE038]">0113 519 7348 <span className="text-[10px] uppercase tracking-widest text-[#6B7C71] ml-1">Landline</span></a>
            </div>
          </div>
          <div className="mt-2 flex items-start gap-2 text-[#9CAEA3]"><Mail size={16} className="mt-1 text-[#8DE038]" /> hello@allgreenconnect.co.uk</div>
        </div>
      </div>
      <div className="border-t border-[#1F3325] py-5 px-5 md:px-8 max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-2">
        <div className="text-xs text-[#6B7C71] font-display uppercase tracking-widest">© {new Date().getFullYear()} {BRAND.name} · Established {BRAND.established}</div>
        <div className="text-xs text-[#6B7C71]">A marketplace for home &amp; garden services.</div>
      </div>
    </footer>
  );
}
